import React, { useState } from 'react';
import { Paper, Typography, Box, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const TransitCalendarLegend = ({ calendarType = 'cosmic-personal-events' }) => {
  const [expanded, setExpanded] = useState(false);

  // Calendar-specific content
  const calendarInfo = {
    'cosmic-personal-events': {
      title: 'Cosmic Personal Events Calendar',
      whatItShows: `Your Cosmic Personal Events Calendar displays the current planetary positions (transits) and how they interact
        with each other throughout the selected period. This calendar shows transit-to-transit aspects - the angles between
        moving planets in the sky - and how they activate specific houses in your natal chart.`,
      keyPoints: [
        '<strong>Current planetary positions</strong> in the zodiac signs',
        '<strong>Transit-to-transit aspects</strong> - angles between moving planets',
        '<strong>Houses occupied</strong> by each transit planet in your natal chart',
        '<strong>Detailed interpretations</strong> of each aspect\'s meaning and timing',
        '<strong>Personal house activations</strong> showing which life areas are energized'
      ]
    },
    'personal-cosmic-activation': {
      title: 'Personal Cosmic Activation',
      whatItShows: `Your Personal Cosmic Activation calendar reveals how current transit-to-transit aspects (aspects between moving
        planets in the sky) activate your natal planets. This powerful tool shows you when current planetary configurations
        "wake up" specific points in your birth chart, creating personalized cosmic activations unique to you.`,
      keyPoints: [
        '<strong>Transit-to-transit aspects</strong> - current aspects between planets in the sky',
        '<strong>Natal planet activations</strong> - which of YOUR birth planets are being triggered',
        '<strong>Personalized manifestations</strong> - how these activations specifically affect YOU',
        '<strong>House-based interpretations</strong> showing which life areas are activated',
        '<strong>Exact timing</strong> of when your natal chart is most powerfully activated'
      ]
    },
    'personal-transit': {
      title: 'Personal Transit Calendar',
      whatItShows: `Your Personal Transit Calendar shows how current planetary positions (transits) directly aspect your natal planets.
        This is the most personal type of transit reading, revealing exact moments when the current sky makes specific geometric
        angles to the planets in your birth chart. Each aspect tells a unique story about your current life journey.`,
      keyPoints: [
        '<strong>Natal-to-transit aspects</strong> - transiting planets aspecting YOUR natal planets',
        '<strong>Personal activations</strong> - which specific parts of your chart are being triggered',
        '<strong>Direct interpretations</strong> for each natal planet-transit combination',
        '<strong>House positions</strong> showing where in your life these energies manifest',
        '<strong>Exact timing</strong> showing when aspects are strongest'
      ]
    }
  };

  const currentCalendar = calendarInfo[calendarType];

  const aspects = [
    { name: 'Conjunction', symbol: '☌', color: '#9370DB', description: 'Planets in the same position (0°). Intense, merging energy.' },
    { name: 'Opposition', symbol: '☍', color: '#FFA500', description: 'Planets 180° apart. Tension, awareness, balance needed.' },
    { name: 'Trine', symbol: '△', color: '#0000FF', description: 'Planets 120° apart. Harmonious, flowing, easy energy.' },
    { name: 'Square', symbol: '□', color: '#FF0000', description: 'Planets 90° apart. Challenge, friction, action required.' },
    { name: 'Sextile', symbol: '⚹', color: '#00FF00', description: 'Planets 60° apart. Opportunity, potential, cooperation.' }
  ];

  const planets = [
    { name: 'Sun', symbol: '☉', color: '#ffde59', description: 'Identity, ego, vitality, core self' },
    { name: 'Moon', symbol: '☽', color: '#C0C0C0', description: 'Emotions, instincts, habits, needs' },
    { name: 'Mercury', symbol: '☿', color: '#00FF41', description: 'Communication, thinking, learning' },
    { name: 'Venus', symbol: '♀', color: '#ff66c4', description: 'Love, values, beauty, relationships' },
    { name: 'Mars', symbol: '♂', color: '#f50909', description: 'Action, energy, desire, courage' },
    { name: 'Jupiter', symbol: '♃', color: '#FF6600', description: 'Growth, expansion, luck, wisdom' },
    { name: 'Saturn', symbol: '♄', color: '#8B4513', description: 'Structure, discipline, lessons, maturity' },
    { name: 'Uranus', symbol: '♅', color: '#00CED1', description: 'Change, innovation, freedom, awakening' },
    { name: 'Neptune', symbol: '♆', color: '#4169E1', description: 'Dreams, spirituality, illusion, compassion' },
    { name: 'Pluto', symbol: '♇', color: '#800080', description: 'Transformation, power, rebirth, depth' }
  ];

  const orbRanges = [
    { type: 'Moon aspects', orb: '1-2°', description: 'Tightest orbs due to the Moon\'s fast movement' },
    { type: 'Inner-to-Inner (Sun-Mars)', orb: '2-3°', description: 'Fast-moving planets require tighter orbs' },
    { type: 'Mixed (Inner-Outer)', orb: '2.5-4°', description: 'Moderate orbs for mixed planet types' },
    { type: 'Outer-to-Outer (Jupiter-Pluto)', orb: '3-5°', description: 'Wider orbs for slow-moving planets' },
    { type: 'Jupiter-Saturn', orb: '4-6°', description: 'Special wider orbs for generational aspects' },
    { type: 'Lunar Nodes', orb: '3-5°', description: 'Karmic points with moderate orbs' }
  ];

  return (
    <Paper
      sx={{
        mb: 3,
        overflow: 'hidden',
        border: '1px solid rgba(102, 126, 234, 0.2)'
      }}
    >
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          '&:before': { display: 'none' },
          boxShadow: 'none'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: 'rgba(102, 126, 234, 0.05)',
            '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              📖 How to Read Your {currentCalendar.title}
            </Typography>
            <Chip
              label={expanded ? "Hide Guide" : "Show Guide"}
              size="small"
              sx={{
                bgcolor: 'rgba(102, 126, 234, 0.2)',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            />
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
          {/* What This Shows */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea', fontSize: { xs: '1rem', sm: '1.125rem' } }}>
              What This Calendar Shows
            </Typography>
            <Typography variant="body2" paragraph sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
              {currentCalendar.whatItShows}
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
              {currentCalendar.keyPoints.map((point, index) => (
                <li key={index} dangerouslySetInnerHTML={{ __html: point }}></li>
              ))}
            </Box>
          </Box>

          {/* How to Use It */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea', fontSize: { xs: '1rem', sm: '1.125rem' } }}>
              How to Use This Calendar
            </Typography>
            <Box component="ol" sx={{ pl: 2, mb: 0, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
              <li><strong>Click any day</strong> to see detailed planetary positions and aspects for that date</li>
              <li><strong>Review aspect interpretations</strong> to understand how the planets interact</li>
              <li><strong>Note the houses</strong> to see which life areas are activated</li>
              <li><strong>Check the timing</strong> - aspects show when they're exact (strongest effect)</li>
              <li><strong>Plan accordingly</strong> - use harmonious days for important activities</li>
            </Box>
          </Box>

          {/* Aspects Legend */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea', fontSize: { xs: '1rem', sm: '1.125rem' } }}>
              Aspect Types
            </Typography>
            <Typography variant="body2" paragraph sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' }, mb: 2 }}>
              Aspects are specific angles between planets that create different types of energy:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {aspects.map(aspect => (
                <Box
                  key={aspect.name}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: { xs: '100%', sm: '180px' }
                  }}>
                    <Box
                      sx={{
                        width: { xs: 28, sm: 32 },
                        height: { xs: 28, sm: 32 },
                        borderRadius: '50%',
                        bgcolor: aspect.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        flexShrink: 0
                      }}
                    >
                      {aspect.symbol}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                      {aspect.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {aspect.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Planets Legend */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea', fontSize: { xs: '1rem', sm: '1.125rem' } }}>
              Planet Meanings
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.5
            }}>
              {planets.map(planet => (
                <Box
                  key={planet.name}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 24, sm: 28 },
                      height: { xs: 24, sm: 28 },
                      borderRadius: '50%',
                      bgcolor: planet.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: planet.name === 'Mercury' ? '#000' : '#fff',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      flexShrink: 0
                    }}
                  >
                    {planet.symbol}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' }, lineHeight: 1.2 }}>
                      {planet.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      {planet.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Orbs Explanation */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea', fontSize: { xs: '1rem', sm: '1.125rem' } }}>
              Understanding Orbs
            </Typography>
            <Typography variant="body2" paragraph sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
              An <strong>orb</strong> is the allowable distance (in degrees) from an exact aspect. Tighter orbs mean
              the aspect must be more precise to be considered active. Different planet combinations use different orbs:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {orbRanges.map((range, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 0.5, sm: 2 },
                    p: 1.5,
                    bgcolor: index % 2 === 0 ? 'rgba(102, 126, 234, 0.05)' : 'transparent',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ minWidth: { xs: '100%', sm: '200px' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                      {range.type}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                      Orb: {range.orb}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {range.description}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
              💡 <strong>Tip:</strong> Aspects become stronger as they approach exactness (0° orb). The interpretation
              shows when each aspect is exact, which is when its energy is most potent.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default TransitCalendarLegend;
